import type { ICustomMessage } from '@/components/chat';
import { messageAttributesSchema } from '@/components/chatHelpers';
import type { InquiryParticipant } from '@/hooks/useChat';
import type { ChatMessage } from '@/services/chat/chatTransport';
import { normalizeMessageAttributes } from '@/types/messages/attributes';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import { Sentry } from '@/util/sentry';
import { stripProductInfoPrefix } from '@/utils/modHelpers';

/**
 * Maps a Twilio message onto the app's chat message model.
 *
 * Attribute reads are deliberately tolerant: a payload that fails validation
 * drops its fields rather than throwing, so an unknown or malformed message
 * type degrades to plain text instead of breaking the thread. The parse
 * failure is reported to Sentry but never propagated.
 *
 * Every gifted-chat import here must stay type-only — the runtime module
 * resolves through directory imports that the test environment cannot load.
 */
export const parseMessage = (
  message: ChatMessage,
  recipient: InquiryParticipant
): ICustomMessage => {
  const user = {
    _id: message.author || '',
    ...(Number(message.author) === recipient.id
      ? {
          name: getProfileNameAndInitials(recipient).name,
          avatar: recipient && getAvatarUrl(recipient.avatar),
        }
      : {}),
  };

  const messageText =
    message.index === 0 ? stripProductInfoPrefix(message.body ?? undefined) : message.body || '';

  const normalizedAttributes = normalizeMessageAttributes(message.attributes);

  const parseResult = messageAttributesSchema.safeParse(normalizedAttributes);

  const parsedAttributes = parseResult.success ? parseResult.data : undefined;

  if (!parseResult.success) {
    Sentry.captureException(parseResult.error, {
      tags: { context: 'parseMessage:attributes' },
      extra: {
        messageSid: message.id,
        attributes: message.attributes,
        normalizedAttributes,
      },
    });
  }

  // The rename is the one field mapping the spread cannot express: the wire
  // key is `pipelineStatus`, every frontend consumer reads `processingStatus`.
  // Destructuring both out here is what keeps the raw keys off the parsed
  // message — `ICustomMessage` omits them, so letting them through the spread
  // would put a key on the model that no consumer expects to read.
  const { pipelineStatus, pipelineStatusAt, ...attributes } = parsedAttributes ?? {};

  return {
    _id: message.index,
    messageId: message.id,
    text: messageText,
    createdAt: message.createdAt || new Date(),
    user,
    sent: true,
    received: true,
    ...attributes,
    processingStatus: pipelineStatus,
    processingStatusAt: pipelineStatusAt,
  };
};
